import { Button, Input, Markdown, Typography } from '@mgmresorts/wcl-lab';
import React, { useContext, useMemo, useEffect } from 'react';

import { Alert } from '../../components/ui/Alert';
import { Container } from '../../components/ui/Container';
import { Link } from '../../components/ui/Link';
import { Logo } from '../../components/ui/Logo';
import { PageLayout } from '../../components/ui/PageLayout';
import { useContent } from '../../hooks/useContent';
import { useFieldValidation } from '../../hooks/useFieldValidation';
import { useInput } from '../../hooks/useInput';
import { useEvent } from '../../utils/analytics/useEvent';
import { usePageLoadEvent } from '../../utils/analytics/usePageLoadEvent';
import { activationCodeValidator } from '../../utils/validators';

import { SignUpFormContext } from './SignUpFormContext';
import StyledContent from './StyledContent';

export function SignUpVerification() {
  usePageLoadEvent('sign up', 'email verification');
  const { signUpCodeVerificationError, verifySignupCode, email, backToSignUp, isLoading } =
    useContext(SignUpFormContext);
  const { validateField, invalidFields } = useFieldValidation([activationCodeValidator]);
  const { bind: bindActivationCode, value: activationCode } = useInput('', 'activationCode', undefined, validateField);
  const { getContentByPath } = useContent();
  const trackEvent = useEvent();

  useEffect(() => {
    trackEvent('identity – email verification - start', 'start email verification process', 'emailverificationstart');
  }, [trackEvent]);

  const errorMessage = useMemo<string>(
    () =>
      invalidFields.includes('activationCode')
        ? !activationCode || activationCode.length === 0
          ? getContentByPath(`signUpVerification.fields.activationCode.required`)
          : getContentByPath(`signUpVerification.fields.activationCode.error`)
        : '',
    [activationCode, getContentByPath, invalidFields],
  );

  return (
    <StyledContent>
      <PageLayout
        title={getContentByPath('signUpVerification.title')}
        description={getContentByPath('signUpVerification.description')}
      >
        <Container height='auto'>
          <Logo className='identity__page-logo' />

          <Typography variant='h1' className='identity__page-heading'>
            {getContentByPath('signUpVerification.title')}
          </Typography>

          {!isLoading && signUpCodeVerificationError && <Alert text={signUpCodeVerificationError} />}

          <Markdown paragraphProps={{ variant: 'paragraph' }} className='identity__verification-description'>
            {(getContentByPath('signUpVerification.body') as string).replace('%email%', email ?? '')}
          </Markdown>

          <Input
            label={getContentByPath('signUpVerification.fields.activationCode.title')}
            id='activationCode'
            testId='activationCode'
            disabled={false}
            aria-label={getContentByPath('signUpVerification.fields.activationCode.label')}
            className={`identity__field identity__field-activation-code ${
              invalidFields.includes('activationCode') ? 'with-error' : ''
            }`}
            error={!!errorMessage}
            hint={errorMessage}
            {...bindActivationCode}
          />

          {activationCode.length === 0 && (
            <Alert type='info' text={getContentByPath('signUpVerification.message')}>
              <Typography variant='paragraph' element='p' margin='0'>
                <Link onClick={backToSignUp}>{getContentByPath('signUpVerification.resend')}</Link>
              </Typography>
            </Alert>
          )}

          <Button
            sticky
            testId='activate-button'
            ariaLabel={getContentByPath('signUpVerification.label')}
            onClick={() => verifySignupCode(activationCode)}
            className='form-button identity__form-submit identity__signup-verify-form-submit'
            disabled={isLoading || !activationCode || invalidFields.includes('activationCode')}
            loading={isLoading}
          >
            <Typography>{getContentByPath('signUpVerification.cta')}</Typography>
          </Button>
        </Container>
      </PageLayout>
    </StyledContent>
  );
}
